import { constants } from '@approvalmax/utils';
import { useMemo } from 'react';
import { useGetWalletsBalances } from 'shared/data/wallets/v1';
import { GetWallets, useGetWallets } from 'shared/data/webApp/v2';

import { WalletWithBalance } from './useRequestWalletsBalances.types';

/**
 * The same logic as in `useWalletAvailableCurrencies` but not require Wallets role to make
 * a `wallets` request
 */
export const useRequestWalletsBalances = ({
    companyId,
    requestId,
    walletFilter,
    needBalance,
}: {
    companyId: string;
    requestId: string;
    walletFilter?: (wallet: GetWallets['response']['items'][number]) => boolean;
    needBalance: boolean;
}) => {
    const { data: wallets, isLoading: isLoadingWallets } = useGetWallets(
        { path: { companyId, requestId } },
        { staleTime: constants.time.minutesMs }
    );
    const {
        data: walletsBalances,
        isLoading: isLoadingWalletsBalances,
        refetch: refetchWalletsBalances,
    } = useGetWalletsBalances(
        { path: { organizationId: companyId } },
        { staleTime: constants.time.minutesMs, enabled: needBalance }
    );

    const walletBalancesMap = useMemo(
        () => new Map((walletsBalances?.items || []).map((item) => [item.currency, item.amount])),
        [walletsBalances]
    );

    const walletsWithBalances = useMemo(
        () =>
            wallets?.items.reduce<WalletWithBalance[]>((acc, wallet) => {
                if (walletFilter && !walletFilter(wallet)) {
                    return acc;
                }

                acc.push({
                    id: wallet.currency,
                    walletId: wallet.id,
                    balance: walletBalancesMap.get(wallet.currency),
                    wallet,
                });

                return acc;
            }, []),
        [wallets?.items, walletFilter, walletBalancesMap]
    );

    return {
        walletsWithBalances,
        isLoading: isLoadingWallets || isLoadingWalletsBalances,
        refetchWalletsBalances,
    };
};
